/**
 * HighlightAutoTest — on-demand Trigger für `runHighlightTest`, scoped auf
 * eine Karte. Ruft POST /api/admin/test-protocols/run-highlight/:card_id und
 * rendert das Ergebnis (Verdict + Reasons + Run-ID) inline.
 *
 * Eingebettet in Phase 2 Step 3 ("Grid-Detection ... — Highlights erstellt"),
 * gleicher Container wie der Audit-Link. Pattern: Inspector-UI-Schritte sind 1:1
 * ident mit Code-Pipeline-Schritten — wenn eine Funktion innerhalb von Step N
 * läuft, muss sie auch im Inspector innerhalb von Step N sichtbar sein.
 *
 * NICHT: menschliche Verdicts erfassen, persistieren im Browser-State, pollen.
 * Endpoint ist synchron (~3–5 s). Wer ein Mini-Protokoll referenzieren will,
 * kopiert die Run-ID. Voll-Detail steht unter /admin/test-protocols/<uuid>.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, type RunHighlightForCardResponse, type HighlightCardResult } from "../../lib/api";

type Phase = "idle" | "loading" | "running" | "done" | "error";

/** Deutsche Erklärung der reject_reasons. Die Werte werden inline aus der
 *  Karte gelesen so dass die Begründung immer mit den exakten Zahlen aus dem
 *  konkreten Run übereinstimmt (nicht aus Erinnerung). */
const REASON_DE: Record<string, (r: HighlightCardResult) => string> = {
  anchor_index_shift: (_r) =>
    `Tag-Index verschoben — Tesseract liest echte Day-Numbers (1,2,3,…) an anderen Y-Positionen `
    + `als das detected Grid erwartet. Per-Day-Match-Ratio < 0.5. Klassischer "Tag 22 zeigt auf 24"-Drift.`,
  insufficient_anchor_coverage: (r) =>
    `Anchor-Coverage zu niedrig (anchors_found=${r.anchors_found} < 8) — `
    + `Tesseract konnte die Tag-Nummern in der linken Spalte nicht zuverlässig lesen. `
    + `Ohne diese externe Truth-Source kann das Grid nicht verifiziert werden. `
    + `Klassischer Fall: Grid liegt komplett verschoben (z.B. 60% rechts statt links), `
    + `Test würde fail-open durchwinken. Fail-closed-Schutz gegen falsche Spalten-Zuordnung.`,
  form_type_unknown: (r) =>
    `Form-Typ konnte nicht eindeutig erkannt werden — Tesseract findet weder `
    + `"Stundenliste" noch "Diäten"-Keywords im Top-Header. Grid-Detector arbeitet `
    + `trotzdem, aber Spalten-Identifikation unverlässlich (User-Korrektur landet in `
    + `falscher Spalte → falsche Abrechnung).`,
  cols_incomplete: (r) => {
    const detected = r.cols_detected ?? [];
    const EXPECTED = ["von1", "bis1", "von2", "bis2", "symb"];
    const missing = EXPECTED.filter((c) => !detected.includes(c));
    return `Spalten unvollständig — nur ${detected.length}/5 erkannt, fehlt: ${missing.join(", ")} (Grid-Detector hat eine Tabellen-Kante verfehlt)`;
  },
  cv_high: (r) =>
    `Zeilenhöhen ungleichmäßig (cv=${(r.cv ?? 0).toFixed(3)}, Schwelle 0.10)`,
  row_count: (r) =>
    `Statt 31 wurden ${r.rows_count} Zeilen erkannt`,
  homography_weak: (r) =>
    `Template-Match zu schwach (inlier_ratio=${(r.inlier_ratio ?? 0).toFixed(3)}, Schwelle 0.20)`,
  bounds_implausible: (r) =>
    `Grid-Position auf der Seite ungewöhnlich (oben=${((r.top_pct ?? 0) * 100).toFixed(1)} %, unten=${((r.bottom_pct ?? 0) * 100).toFixed(1)} %)`,
  anchor_mismatch: (r) => {
    const dTop = Math.abs((r.top_pct ?? 0) - (r.anchor_top_pct ?? 0));
    const dBot = Math.abs((r.bottom_pct ?? 0) - (r.anchor_bot_pct ?? 0));
    return `Bbox-Grid passt nicht zu den gedruckten Tag-Zahlen (Δoben=${(dTop * 100).toFixed(2)} %, Δunten=${(dBot * 100).toFixed(2)} %, Schwelle 1.5 %)`;
  },
  page_skewed: (r) =>
    `Seite ist verdreht (${(r.skew_angle_deg ?? 0) >= 0 ? "+" : ""}${(r.skew_angle_deg ?? 0).toFixed(2)}°, Schwelle 0.8°) — Highlight-Overlay driftet am rechten Seitenrand`,
  // ─── Diäten-Validator (Gridlinien-Truth, 2026-06-09) ───
  spatial_missing: (_r) =>
    `Kein Template-Match — die Karte matcht das Diäten-Template nicht (ORB/RANSAC unter Schwelle). `
    + `Ohne Homography gibt es keine projizierten Zeilen/Spalten → Highlights wären reine Heuristik. `
    + `Typisch: untypischer Scan, falsches Formular oder extreme Bildqualität.`,
  gridline_mismatch: (r) =>
    `Projizierte Zeilen liegen NICHT auf den gedruckten Tabellenlinien — nur ${r.anchors_found} Grenzen `
    + `auf echten Linien (Median-Abweichung ${r.anchor_top_pct !== null ? (r.anchor_top_pct * 100).toFixed(2) + " %" : "n/a"}, `
    + `Schwelle 1 % Seitenhöhe). Das Highlight würde auf falsche Zeilen zeigen → Vergleichen sperren.`,
  gridline_coverage_low: (r) =>
    `Zu wenige gedruckte Tabellenlinien erkennbar (lines_found < 8) — die unabhängige Truth-Quelle fehlt, `
    + `die Projektion kann nicht verifiziert werden. Fail-closed (gleiches Prinzip wie `
    + `insufficient_anchor_coverage bei der Stundenliste).`,
};

export function HighlightAutoTest({ cardId }: { cardId: string }) {
  const [phase, setPhase] = useState<Phase>("loading");
  const [result, setResult] = useState<RunHighlightForCardResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Letzten persistierten Lauf laden — das Ergebnis bleibt über Page-Reload
  // sichtbar (Mini-Protokoll lebt in test_run_protocols, Frontend rehydriert).
  useEffect(() => {
    let cancelled = false;
    setPhase("loading");
    api.testProtocols.getLatestHighlightForCard(cardId)
      .then((r) => {
        if (cancelled) return;
        if (r) { setResult(r); setPhase("done"); }
        else { setPhase("idle"); }
      })
      .catch(() => { if (!cancelled) setPhase("idle"); });
    return () => { cancelled = true; };
  }, [cardId]);

  async function run() {
    setPhase("running");
    setError(null);
    try {
      const r = await api.testProtocols.runHighlightForCard(cardId);
      setResult(r);
      setPhase("done");
    } catch (e: any) {
      setError(e?.message ?? String(e));
      setPhase("error");
    }
  }

  async function copyRunId() {
    if (!result?.id) return;
    await navigator.clipboard.writeText(result.id);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <button
        type="button"
        onClick={run}
        disabled={phase === "running" || phase === "loading"}
        style={{
          padding: "5px 12px", fontSize: 12, fontWeight: 600,
          background: phase === "running" || phase === "loading" ? "#9ca3af" : "#1d4ed8",
          color: "white", border: 0, borderRadius: 4,
          cursor: phase === "running" || phase === "loading" ? "wait" : "pointer",
          alignSelf: "flex-start",
        }}
        title="Startet den Highlight-Validator (5 Signale, ~3–5 s). Ergebnis persistiert in test_run_protocols."
      >
        {phase === "loading"
          ? "lade letzten Lauf …"
          : phase === "running"
            ? "läuft …"
            : phase === "done"
              ? "🔁 Erneut prüfen"
              : "Highlight automatisch prüfen"}
      </button>

      {phase === "error" && error && (
        <div style={{ color: "#991b1b", fontSize: 13 }}>Fehler: {error}</div>
      )}

      {phase === "done" && result && (
        <ResultPanel result={result} onCopy={copyRunId} copied={copied} />
      )}
    </div>
  );
}

function ResultPanel({
  result, onCopy, copied,
}: {
  result: RunHighlightForCardResponse;
  onCopy: () => void;
  copied: boolean;
}) {
  const v = result.verdict;
  const valid = v === "VALID";
  // Skip-Reasons (z.B. NON_STUNDENLISTE_FORM) sind kein Fehlschlag — die Karte
  // ist nur außerhalb des Test-Scopes. Neutraler Ton damit "out of scope" nicht
  // wie "FAIL" gelesen wird.
  const skipped = !valid && !!result.skipped && !result.card_result;
  const tone = valid
    ? { bg: "#ecfdf5", fg: "#065f46", border: "#86efac", icon: "✅" }
    : skipped
      ? { bg: "#f3f4f6", fg: "#374151", border: "#d1d5db", icon: "⏭" }
      : { bg: "#fef2f2", fg: "#991b1b", border: "#fca5a5", icon: "❌" };
  const cr = result.card_result;
  const reasons = cr?.reject_reasons ?? [];

  return (
    <div style={{
      background: tone.bg, color: tone.fg, border: `1px solid ${tone.border}`,
      padding: 10, borderRadius: 4, fontSize: 13,
      display: "flex", flexDirection: "column", gap: 6,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12 }}>
        <strong>{tone.icon} {v}</strong>
        <span
          style={{ fontFamily: "monospace", fontSize: 12, opacity: 0.8, cursor: "pointer" }}
          onClick={onCopy}
          title="Run-ID in Clipboard kopieren (kann dem Agent referenziert werden)"
        >
          Run-ID: {result.id.slice(0, 8)}… {copied ? "✓ kopiert" : "📋"}
        </span>
      </div>
      {result.run_at && (
        <div style={{ fontSize: 11, opacity: 0.7 }}>
          Lauf vom {new Date(result.run_at).toLocaleString("de-AT")}
        </div>
      )}
      {cr && reasons.length > 0 && (
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {reasons.map((r) => (
            <li key={r}>{REASON_DE[r]?.(cr) ?? r}</li>
          ))}
        </ul>
      )}
      {result.skipped && (
        <div>
          Übersprungen: {result.skipped.reason}
          {result.skipped.detail ? ` — ${result.skipped.detail}` : ""}
        </div>
      )}
      <Link to={`/admin/test-protocols/${result.id}`} style={{ fontSize: 12, color: tone.fg }}>
        Voll-Detail (JSON) →
      </Link>
    </div>
  );
}
